import React from "react";
import styled from "styled-components";
import { Tag } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { formatFilterDisplayValue, OPERATOR_SHORT } from "../../utils/tableFilters";

const Row = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 10px;
    padding: 10px 20px;
    border-bottom: 1px dashed var(--app-border);
    background: color-mix(in srgb, #dc3545 5%, var(--app-surface));

    .applied-filters-row__label {
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: var(--app-text-muted);
        flex-shrink: 0;
        margin-right: 4px;
    }

    .applied-filters-row__chips {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
        flex: 1;
        min-width: 0;
    }

    .applied-filters-chip.ant-tag {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin: 0;
        padding: 4px 10px;
        border-radius: 16px;
        font-size: 12px;
        line-height: 1.4;
        border: 1px solid color-mix(in srgb, #dc3545 28%, var(--app-surface));
        background: color-mix(in srgb, #dc3545 8%, var(--app-surface));
        color: var(--app-text);
    }

    .applied-filters-chip .applied-filters-chip__column {
        font-weight: 600;
    }

    .applied-filters-chip .applied-filters-chip__operator {
        font-size: 11px;
        font-weight: 600;
        color: var(--app-text-muted);
        background: var(--app-surface-alt);
        padding: 1px 6px;
        border-radius: 4px;
    }

    .applied-filters-chip .applied-filters-chip__value {
        font-weight: 700;
        color: #dc3545;
        max-width: 220px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .applied-filters-chip .applied-filters-chip__close {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-left: 2px;
        padding: 0;
        border: none;
        background: transparent;
        color: var(--app-text-muted);
        cursor: pointer;
        line-height: 1;
        font-size: 10px;

        &:hover {
            color: #dc3545;
        }
    }

    .applied-filters-row__clear {
        flex-shrink: 0;
        margin-left: auto;
        font-size: 12px;
        font-weight: 600;
        color: var(--app-text-muted);
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        outline: none;
        padding: 2px 0;
        cursor: pointer;
        transition: color 0.15s;

        &:hover,
        &:focus,
        &:focus-visible {
            color: #f45b68;
            text-decoration: none;
            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
        }
    }
`;

/**
 * Removable filter chips shown below the table toolbar when filters are active.
 *
 * @param {{
 *   activeFilters: Array<{ id: string, columnLabel?: string, column?: string, operator?: string, value?: unknown }>,
 *   onRemove: (id: string) => void,
 *   onClear?: () => void,
 *   className?: string,
 * }} props
 */
export default function AppliedFiltersRow({ activeFilters, onRemove, onClear, className }) {
    if (!activeFilters?.length) return null;

    return (
        <Row className={className ? `applied-filters-row ${className}` : "applied-filters-row"}>
            <span className="applied-filters-row__label">Applied Filters</span>
            <div className="applied-filters-row__chips">
                {activeFilters.map((filter) => {
                    const columnLabel = filter.columnLabel || filter.column || "Filter";
                    const operator = filter.operator ? (OPERATOR_SHORT[filter.operator] || filter.operator) : null;
                    const value = formatFilterDisplayValue(filter);

                    return (
                        <Tag key={filter.id} className="applied-filters-chip" bordered={false}>
                            <span className="applied-filters-chip__column">{columnLabel}</span>
                            {operator ? <span className="applied-filters-chip__operator">{operator}</span> : null}
                            <span className="applied-filters-chip__value" title={value}>{value}</span>
                            <button
                                type="button"
                                className="applied-filters-chip__close"
                                onClick={() => onRemove(filter.id)}
                                aria-label={`Remove filter for ${columnLabel}`}
                            >
                                <CloseOutlined />
                            </button>
                        </Tag>
                    );
                })}
            </div>
            {typeof onClear === "function" ? (
                <button type="button" className="applied-filters-row__clear" onClick={onClear}>
                    Clear all
                </button>
            ) : null}
        </Row>
    );
}
